import { supabase } from '@/integrations/supabase/client';
import { getOrCreateAgentProfile, getAllConversationsForUser, getEnrichedProductsForAgent } from './askCircle';
import { getTechStackSummary } from './techStack';
import { getRecentlyViewed } from './browsingActivity';
import { membershipService } from './membership';

export interface DashboardData {
  profile: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  agentProfile: {
    career_stage: string | null;
    years_in_real_estate: number | null;
    deals_closed_last_year: number | null;
    primary_market: string | null;
    goals: any[] | null;
  } | null;
  membership: {
    tier: string;
    is_pro: boolean;
    is_founder: boolean;
  };
  usageStats: {
    dailyCount: number;
    dailyLimit: number;
    tier: string;
  } | null;
  techStackSummary: {
    total_items: number;
    owned_count: number;
    total_monthly_cost: number;
  };
  favorites: Array<{
    id: string;
    slug: string;
    title: string;
    image: string | null;
    price: number | null;
    rating: number | null;
    subtitle: string | null;
  }>;
  recentlyViewed: Array<{
    product_id: string;
    created_at: string;
    products: {
      id: string;
      slug: string;
      title: string;
      image: string | null;
      price: number | null;
      rating: number | null;
    };
  }>;
  recentConversations: Array<{
    id: string;
    started_at: string;
    last_message_at: string;
    conversation_summary: string | null;
  }>;
  recommendedProducts: Array<{
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    image: string | null;
    price: number | null;
    rating: number | null;
    categories: any;
    relevance_score: number;
  }>;
  hotProducts: Array<{
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    image: string | null;
    price: number | null;
    msrp: number | null;
    rating: number | null;
    review_count: number | null;
    categories: any;
    is_hot: boolean;
    is_select: boolean;
  }>;
}

/**
 * Dashboard data facade - fetches all dashboard data in PARALLEL
 * Implements Engineering Playbook pattern: thin UI, fat services, cacheable
 */
export async function getDashboardData(userId: string): Promise<DashboardData> {
  const [
    profileRes,
    agentProfile,
    membershipRes,
    usageStatsRes,
    techStackSummary,
    favoritesRes,
    recentlyViewed,
    conversationsRes,
    hotProductsRes,
  ] = await Promise.all([
    // Profile
    supabase
      .from('profiles')
      .select('full_name, avatar_url')
      .eq('user_id', userId)
      .single(),
    
    // Agent profile
    getOrCreateAgentProfile(userId),
    
    // Membership
    membershipService.getMembership(userId),
    
    // AskCircle usage stats
    supabase
      .from('askcircle_usage')
      .select('message_count')
      .eq('user_id', userId)
      .gte('period_start', new Date().toISOString().split('T')[0])
      .single(),
    
    // Tech stack summary
    getTechStackSummary(userId),
    
    // Favorites (limit to 6 for shortlist panel)
    supabase
      .from('user_favorites')
      .select(`
        products (
          id,
          slug,
          title,
          image,
          price,
          rating,
          subtitle
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(6),
    
    // Recently viewed
    getRecentlyViewed(userId, 5),
    
    // Recent conversations (last 3)
    getAllConversationsForUser(userId),
    
    // Hot products
    supabase
      .from('products')
      .select('id, slug, title, subtitle, image, price, msrp, rating, review_count, categories, is_hot, is_select')
      .eq('is_published', true)
      .eq('is_hot', true)
      .order('sort_order', { ascending: true })
      .limit(20),
  ]);

  // Get usage limits from system config
  const dailyLimitRes = await supabase
    .from('system_config')
    .select('value')
    .eq('key', 'askcircle_quota')
    .maybeSingle();

  const quotaConfig = dailyLimitRes.data?.value as any;
  const isPro = membershipService.isPro(membershipRes);
  const isFounder = membershipService.isFounder(membershipRes);
  
  const tier = isFounder ? 'founder' : isPro ? 'pro' : 'free';
  const dailyLimit = quotaConfig?.[tier]?.daily ?? 5;
  const dailyCount = usageStatsRes.data?.message_count ?? 0;

  // Get recommended products using enrichment engine
  const enrichedProducts = await getEnrichedProductsForAgent(agentProfile);
  const topRecommendations = enrichedProducts.slice(0, 6);

  return {
    profile: profileRes.data,
    agentProfile: agentProfile ? {
      career_stage: agentProfile.career_stage,
      years_in_real_estate: agentProfile.years_in_real_estate,
      deals_closed_last_year: agentProfile.deals_closed_last_year,
      primary_market: agentProfile.primary_market,
      goals: agentProfile.goals as any[] | null,
    } : null,
    membership: {
      tier,
      is_pro: isPro,
      is_founder: isFounder,
    },
    usageStats: {
      dailyCount,
      dailyLimit,
      tier,
    },
    techStackSummary: techStackSummary ? {
      total_items: techStackSummary.total || 0,
      owned_count: techStackSummary.owned || 0,
      total_monthly_cost: techStackSummary.totalMonthlyCost || 0,
    } : {
      total_items: 0,
      owned_count: 0,
      total_monthly_cost: 0,
    },
    favorites: (favoritesRes.data || []).map((f: any) => f.products).filter(Boolean),
    recentlyViewed: recentlyViewed || [],
    recentConversations: (conversationsRes || []).slice(0, 3),
    recommendedProducts: topRecommendations.map((p) => ({
      id: p.id,
      slug: p.slug,
      title: p.title,
      subtitle: p.subtitle || null,
      image: p.image || null,
      price: p.price || null,
      rating: p.rating || null,
      categories: p.categories,
      relevance_score: p.relevance_score,
    })),
    hotProducts: hotProductsRes.data || [],
  };
}
